// @flow
import React, { useState } from 'react';

import { FacetCategory } from 'Components/widgets/criteria/criteria-facets/search-facets/FacetCategory';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';
import { Tabs, type TabsItem } from 'Components/frameworks/tabs/Tabs.jsx';
import { Tag } from 'Components/frameworks/tag/Tag.jsx';
import { TemplateString } from 'Components/frameworks/template-string/TemplateString.jsx';

import { ICONS } from 'Components/utils/icon/icons';
import { getActiveFacetTabTrackingData } from 'Components/widgets/criteria/criteria-facets/search-facets/SearchFacets.utils';
import { t } from 'Src/i18n';
import { clearFacets, toggleFacet } from 'Store/faceting/facetingSearch.action';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { type FacetTemplateConfig, type FacetTrackingConfig } from 'Utils/faceting/constants';

export type Props = {
  activeFacets: *,
  facets: Array<SearchFacetCategory>,
  clearFacets: typeof clearFacets,
  toggleFacet: typeof toggleFacet,
  facetTemplateConfig: Array<FacetTemplateConfig>,
  facetTrackingConfig: Array<FacetTrackingConfig>,
};

export const SearchFacets = (props: Props) => {
  const { activeFacets, facets, facetTemplateConfig = [], facetTrackingConfig } = props;
  const [selectedFacetTab, setSelectedFacetTab] = useState(-1);

  const track = useTracking();

  if (facets.length <= 0) {
    return null;
  }

  const onClearFacets = (id: string) => {
    const templateConfig = facetTemplateConfig.find(({ id: facetId }) => id === facetId);

    if (!templateConfig) {
      return;
    }

    props.clearFacets(id);

    templateConfig.facetLists.forEach(({ id: facetListId }) => {
      props.clearFacets(facetListId);
    });
  };

  const trackFacetChange = (name: string, checked: boolean, value: string | boolean) => {
    if (!checked) {
      return;
    }
    const labelSuffix = value
      .toString()
      .replace(/[|\s]/g, '_')
      .toLowerCase();

    const trackingConfig = facetTrackingConfig.find(({ id }) => id === name.replace('[]', ''));

    if (!trackingConfig) {
      return;
    }

    const labelGA = `${trackingConfig.trackingPrefixLabel}_${labelSuffix}`;
    if (labelGA) {
      track({
        actionGA: 'smartCriterias',
        labelGA,
      });
    }
  };

  const changeFacet = (name: string, checked: boolean, value: string | boolean) => {
    setSelectedFacetTab(-1);
    trackFacetChange(name, checked, value);
    props.toggleFacet({
      categoryId: name,
      isSelected: checked,
      value,
    });
  };

  const getTabItems = (): Array<$Shape<TabsItem>> => {
    return facetTemplateConfig.map(templateConfig => {
      const { id, dataLabel, iconName, titleLabel, facetLists } = templateConfig;
      const count = facetLists
        .map(
          ({ id: facetId }) =>
            activeFacets[facetId] ? Object.keys(activeFacets[facetId]).length : 0,
        )
        .reduce((acc, facetCount) => acc + facetCount, 0);

      const title = t(titleLabel);

      return {
        id,
        header: {
          iconName,
          title,
          trackingData: getActiveFacetTabTrackingData({ facetId: dataLabel }),
        },
        customHeader:
          count > 0
            ? (
                { controlsId, id: itemId, onClick, onKeyDown, isActive, style, tabIndex },
                setRef,
              ) => (
                <div className="px-3 py-2 decorator-separator-left" key={title} style={style}>
                  <Tag
                    aria-controls={controlsId}
                    area-selected={String(isActive)}
                    badge={
                      <TemplateString
                        staticClassName="a11y"
                        template={
                          count > 1
                            ? t('common.selectedCriteriasLabel.n')
                            : t('common.selectedCriteriasLabel._1')
                        }
                        values={{ count: count.toString() }}
                      />
                    }
                    closeLabel={`${t('faceting.reset')} ${title}`}
                    id={itemId}
                    label={title}
                    onClick={onClick}
                    onClose={() => onClearFacets(id)}
                    onKeyDown={onKeyDown}
                    ref={setRef}
                    role="tab"
                    tabIndex={tabIndex}
                    trackingData={
                      !isActive
                        ? getActiveFacetTabTrackingData({
                            facetId: dataLabel,
                            isActive,
                          })
                        : null
                    }
                  />
                </div>
              )
            : null,
        content: [
          <FacetCategory
            templateConfig={templateConfig}
            facetsFromSearch={facets}
            onChange={changeFacet}
            activeFacets={activeFacets}
            key={id}
            name={id}
          />,
        ],
      };
    });
  };

  const onClickIconFilters = () => {
    // Set the first tab as active or close it if already active
    setSelectedFacetTab(selectedFacetTab === 0 ? -1 : 0);
  };

  const onActiveFacetTabChanged = (index: number) => {
    setSelectedFacetTab(index);
  };

  return (
    <Tabs
      areAllCollapsable
      className="mb-6"
      collapseIconName={ICONS.SELECT_UP}
      expandIconName={ICONS.SELECT}
      hasBackground
      initialSelectedItem={selectedFacetTab}
      isContentUnmountedOnCollapse
      isScrollable
      items={getTabItems()}
      onSelect={onActiveFacetTabChanged}
      scrollBackwardLabel={t('common.scrollBackward')}
      scrollForwardLabel={t('common.scrollForward')}
      slotLeft={
        <button
          className="bg-transparent leading-none p-2"
          onClick={onClickIconFilters}
          type="button"
          data-testid="search-facets-toggle-button"
        >
          <Icon name={ICONS.SETTINGS} />
        </button>
      }
      theme="clean"
    />
  );
};
